export type EventCallback<T extends any[]> = (...args: T) => void;

export class EventEmitter<Events extends Record<string | symbol, any[]>> {
  private eventMap = new Map<keyof Events, Set<EventCallback<any[]>>>();
  on<E extends keyof Events>(eventName: E, listener: EventCallback<Events[E]>): this {
    const handlers = this.eventMap.get(eventName) ?? new Set<EventCallback<any[]>>();
    handlers.add(listener);
    this.eventMap.set(eventName, handlers);
    return this;
  }
  emit<E extends keyof Events>(eventName: E, ...args: Events[E]): boolean {
    const handlers = this.eventMap.get(eventName);
    if (!handlers || handlers.size === 0) return false;
    for (const handler of handlers) {
      try {
        handler(...args);
      } catch (error) {
        console.error(`Error in event handler for '${String(eventName)}':`, error);
      }
    }
    return true;
  }
  off<E extends keyof Events>(eventName: E, listener: EventCallback<Events[E]>): this {
    const handlers = this.eventMap.get(eventName);
    if (handlers) {
      handlers.delete(listener);
      if (handlers.size === 0) {
        this.eventMap.delete(eventName);
      }
    }
    return this;
  }
  once<E extends keyof Events>(eventName: E, listener: EventCallback<Events[E]>): this {
    const onceFn: EventCallback<Events[E]> = (...args) => {
      this.off(eventName, onceFn);
      listener(...args);
    };
    return this.on(eventName, onceFn);
  }
  removeAll(eventName?: keyof Events): this {
    if (eventName !== undefined) {
      this.eventMap.delete(eventName);
    } else {
      this.eventMap.clear();
    }
    return this;
  }
  listenerCount<E extends keyof Events>(eventName: E): number {
    return this.eventMap.get(eventName)?.size ?? 0;
  }
  eventNames(): (keyof Events)[] {
    return Array.from(this.eventMap.keys());
  }
  hasListeners<E extends keyof Events>(eventName: E): boolean {
    const handlers = this.eventMap.get(eventName);
    return handlers !== undefined && handlers.size > 0;
  }
}
